import { DatalayerLinkTypes } from '@/datalayer/App.datalayer.constants';
import { GlobalHeader } from '@/features/GlobalHeaderContainer';
import { Language } from '@/features/GlobalHeaderContainer/GlobalHeader/GlobalHeader.interface';
import Button from '@/features/shared/Button';
import { Jumplinks } from '@/features/shared/Jumplinks';
import { MuiButton } from '@/features/shared/material-ui';
import { AppContext } from '@/store';
import { Theme } from '@/utils/constants';
import { jumpLinkScrollTo } from '@/utils/helpers';
import { resolveAlternativeLanguageCodes } from '@/utils/resolvers/translation';
import clsx from 'clsx';
import { isEmpty } from 'lodash/fp';
import React, { memo, PropsWithChildren, useContext, useMemo } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { CampaignHeaderProps } from './CampaignHeader.interface';
import useStyles from './CampaignHeader.styles';

const scrollTo = (identifier: string) => {
  const anchor = document.querySelector(`#${identifier}`) as Element;
  anchor?.scrollIntoView({
    behavior: 'smooth',
  });
};

const handleKeyDown = (event: KeyboardEvent, target: string) => {
  const isSpacebar = /^ $/i.test(event.key);
  const isEnter = /^enter$/i.test(event.key);

  if (!isSpacebar && !isEnter) {
    return;
  }

  event.preventDefault();
  scrollTo(target);
};

const withHref =
  ({ href }: any) =>
  ({ children }: PropsWithChildren<any>) => {
    if (href) {
      return <a href={href}>{children}</a>;
    }
    return <>{children}</>;
  };

const CampaignLogo = ({ logo, logoUrl, logoPath, logoAlt, className }: PropsWithClassName<any>) => {
  const ImageWrapper = useMemo(
    () => withHref({ hasHref: logo && logoUrl, href: logoUrl }),
    [logo, logoUrl],
  );

  return logo && logoPath ? (
    <div className={className}>
      <ImageWrapper>
        <img src={logoPath} alt={logoAlt} />
      </ImageWrapper>
    </div>
  ) : null;
};

const CampaignHeader = ({
  content = {},
  presentation = {},
  languages,
  config,
  labels,
  elasticsearchConfig,
  marketoConfig,
  contentButtons,
  translationConfig,
  languageConfig,
}: PropsWithChildren<Br.PropsWithContentButtons<CampaignHeaderProps>>) => {
  const [languageList, setLanguageList] = React.useState<Language[]>([]);

  const { buttons, logoOne, logoTwo, logoThree } = content;
  const classes = useStyles();

  const { isTransparent, includeLanguageDropdown, includeJumplinks } = presentation;

  const {
    state: { hrefLang, jumplinks: pageJumplinks },
  } = useContext(AppContext);

  const languageListDefault: Language[] = useMemo(() => {
    if (!languages || !includeLanguageDropdown) return [];
    return Object.entries(languages).map(([key, value]) => {
      const SEPARATOR = '|';
      const [label = '', url = ''] = (value as string).split(SEPARATOR);

      return {
        initial: key,
        displayText: label,
        url,
      };
    });
  }, [includeLanguageDropdown, languages]);

  const alternativeLanguageCodes: Record<string, string> = useMemo(
    () => resolveAlternativeLanguageCodes({ raw: translationConfig }),
    [translationConfig],
  );

  useDeepCompareEffect(() => {
    if (!hrefLang.length) return setLanguageList([...languageListDefault]);
    const translatedPages = languageListDefault.reduce((acc: Language[], curr: Language) => {
      const currPage = hrefLang.find((record) => {
        const langCode = alternativeLanguageCodes[record.hreflang] || record.hreflang;
        return curr.initial === langCode;
      });
      if (!currPage) return [...acc, curr];

      return [...acc, { ...curr, url: currPage.href }];
    }, []);

    return setLanguageList([...translatedPages]);
  }, [languageListDefault, hrefLang]);

  const logoOneImgPath = logoOne && logoOne?.original?.url;
  const logoTwoImgPath = logoTwo && logoTwo?.original?.url;
  const logoThreeImgPath = logoThree && logoThree?.original?.url;
  const logoOneImgUrl = logoOne && logoOne?.url;
  const logoTwoImgUrl = logoTwo && logoTwo?.url;
  const logoThreeImgUrl = logoThree && logoThree?.url;
  const logoOneImgAlt = logoOne && logoOne?.altText;
  const logoTwoImgAlt = logoTwo && logoTwo?.altText;
  const logoThreeImgAlt = logoThree && logoThree?.altText;

  const CampaignLogos = () => {
    return (
      <div className={classes.logos}>
        <CampaignLogo
          className={classes.logoStyles}
          logo={logoOne}
          logoUrl={logoOneImgUrl}
          logoPath={logoOneImgPath}
          logoAlt={logoOneImgAlt}
        />
        <CampaignLogo
          className={classes.logoStyles}
          logo={logoTwo}
          logoUrl={logoTwoImgUrl}
          logoPath={logoTwoImgPath}
          logoAlt={logoTwoImgAlt}
        />
        <CampaignLogo
          className={classes.logoStyles}
          logo={logoThree}
          logoUrl={logoThreeImgUrl}
          logoPath={logoThreeImgPath}
          logoAlt={logoThreeImgAlt}
        />
      </div>
    );
  };
  const campaignButtons = buttons || [];
  const mobileButtons = campaignButtons && campaignButtons.length > 1;
  const CampaignButtons = () => {
    // ignoring the event for position campaign_header because it is caught in app.datalayer
    return (
      <div className={classes.buttons}>
        {campaignButtons?.map((button: any, index: number) => (
          <div className="nav-btn" key={`${index}_${JSON.stringify(button)}`}>
            <Button
              theme={Theme.dark}
              link={button}
              data-datalayer-ignore-link-position="campaign_header"
            />
          </div>
        ))}
      </div>
    );
  };

  const CampaignJumplinks = () => {
    return (
      <ul>
        {pageJumplinks &&
          pageJumplinks.map((item: any, i) => {
            return (
              <li role="menuitem" key={`button_${i + 1}`}>
                <MuiButton
                  className={item.href}
                  variant="text"
                  tabIndex={0}
                  onClick={(event: any) => {
                    event.preventDefault();
                    jumpLinkScrollTo({ event, href: item.href });
                  }}
                  onKeyDown={(ev: any) => handleKeyDown(ev, item.href)}
                  data-datalayer-link-type={DatalayerLinkTypes.LINK_TO_SCROLL}
                  data-datalayer-link
                  data-datalayer-ignore-cta
                >
                  {item.label}
                </MuiButton>
              </li>
            );
          })}
      </ul>
    );
  };

  // Preventing displaying the burger button in mobile if there is no content to display
  const hideMobileNavigationButton = useMemo(
    () => isEmpty(languageList) && isEmpty(pageJumplinks),
    [languageList, pageJumplinks],
  );

  return (
    <GlobalHeader
      isTransparent={isTransparent}
      hasProgressBar={false}
      languageList={languageList}
      elasticsearchConfig={elasticsearchConfig}
      marketoConfig={marketoConfig}
      config={config || {}}
      labels={labels || {}}
      campaign
      languageConfig={languageConfig}
      hideMobileNavigationButton={hideMobileNavigationButton}
    >
      <div slot="menuList">{contentButtons?.content}</div>
      <div slot="campaign-logos" className={classes.campaignLogos}>
        <CampaignLogos />
      </div>
      {includeJumplinks && (
        <div slot="campaign-links" className={classes.navJumplinks}>
          <CampaignJumplinks />
        </div>
      )}
      <div slot="campaign-logos-mobile">
        <CampaignLogos />
      </div>
      {includeJumplinks && (
        <div slot="campaign-links-mobile" className={classes.mobileLinks}>
          <Jumplinks title="" theme={Theme.dark} elements={pageJumplinks} />
        </div>
      )}
      <div
        slot="campaign-buttons"
        className={clsx(mobileButtons ? classes.buttons2 : '', 'gtm-campaign-header-cta')}
      >
        <CampaignButtons />
      </div>
      {mobileButtons && (
        <div
          slot="campaign-buttons-mobile"
          className={clsx(classes.buttonsMobile, 'gtm-campaign-header-cta')}
        >
          <CampaignButtons />
        </div>
      )}
    </GlobalHeader>
  );
};

export default memo(CampaignHeader);
